<template>
  <Drawer
    v-for="item in drawers"
    :key="item.id"
    :visible="isActiveTab(item)"
    :title="item.title"
    placement="right"
    width="95%"
    :mask="isTopOfActiveTab(item)"
    :mask-closable="true"
    :destroy-on-close="false"
    :force-render="true"
    :z-index="BASE_Z_INDEX + item.stackIndex"
    :get-container="resolveContainer"
    wrapper-class-name="page-content-drawer-portal"
    :wrap-class-name="wrapClassName(item)"
    :style="{ position: 'absolute' }"
    :drawer-style="{ position: 'absolute' }"
    :body-style="{ padding: 0, height: '100%', overflow: 'hidden' }"
    @close="closeOne(item.id)"
  >
    <template v-if="item.opt?.renderExtra" #extra>
      <DrawerExtra :render="item.opt.renderExtra" />
    </template>
    <PageContentDrawerPanel :item="item" @close="closeOne(item.id)" />
  </Drawer>
</template>

<script lang="ts" setup>
  import { defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import type { VNodeChild } from 'vue';
  import { useRoute } from 'vue-router';
  import { Drawer } from 'ant-design-vue';
  import PageContentDrawerPanel from './PageContentDrawerPanel.vue';
  import emitter from '@/events/eventBus';

  type ExtraRender = () => VNodeChild;

  /** 抽屉会话扩展（如 flowReviewEdit / approveUrl / busId / gridKey / renderExtra） */
  type DrawerOpt = {
    renderExtra?: ExtraRender;
    [key: string]: any;
  };

  /** 打开抽屉载荷：核心字段 + opt */
  type OpenPayload = {
    pageCode: string;
    moduleCode: string;
    title?: string;
    /** @deprecated 以宿主 useRoute().fullPath 为准 */
    fullPath?: string;
    opt?: DrawerOpt;
  };

  type DrawerItem = {
    id: string;
    pageCode: string;
    moduleCode: string;
    title: string;
    fullPath: string;
    /** 同页签内叠层顺序 */
    stackIndex: number;
    opt: DrawerOpt;
  };

  /** 用函数渲染头部额外区（emitter 无法传真实 Vue slot） */
  const DrawerExtra = defineComponent({
    name: 'PageContentDrawerExtra',
    props: {
      render: {
        type: Function,
        required: true,
      },
    },
    setup(props) {
      return () => (props.render as ExtraRender)();
    },
  });

  const props = defineProps<{
    /** 抽屉挂载容器；不传则挂主布局内容区 #PageLayout */
    getContainer?: () => HTMLElement | false;
  }>();

  /** 需高于 #PageLayout 内业务页（如 PageLayout z-index:100），才能罩住页签内容 */
  const BASE_Z_INDEX = 110;
  let seq = 0;

  const route = useRoute();
  const drawers = ref<DrawerItem[]>([]);
  /** 切页签恢复 visible 时短暂关闭动画，避免像重新打开 */
  const suppressAnim = ref(false);

  const isActiveTab = (item: DrawerItem) => item.fullPath === route.fullPath;

  /** 仅当前页签下的栈顶需要 mask */
  const isTopOfActiveTab = (item: DrawerItem) => {
    if (item.fullPath !== route.fullPath) return false;
    const list = drawers.value.filter((d) => d.fullPath === item.fullPath);
    return list.length > 0 && list[list.length - 1]?.id === item.id;
  };

  const wrapClassName = (item: DrawerItem) => {
    const parts = ['page-content-drawer-wrap'];
    parts.push(isActiveTab(item) ? 'is-active' : 'is-parked');
    if (suppressAnim.value) parts.push('is-no-anim');
    return parts.join(' ');
  };

  const resolveContainer = () => {
    if (props.getContainer) {
      const el = props.getContainer();
      if (el) return el;
    }
    const pageLayout = document.getElementById('PageLayout');
    if (pageLayout) return pageLayout;
    return false;
  };

  const show = (payload: OpenPayload) => {
    const page = String(payload?.pageCode ?? '').trim();
    const module = String(payload?.moduleCode ?? '').trim();
    if (!page || !module) return;
    const t = String(payload?.title ?? '').trim();
    const fullPath = String(route.fullPath ?? '').trim();
    if (!fullPath) return;
    const stackIndex = drawers.value.filter((d) => d.fullPath === fullPath).length;
    seq += 1;
    const rawOpt = payload?.opt && typeof payload.opt === 'object' ? payload.opt : {};
    const opt: DrawerOpt = {
      ...rawOpt,
      renderExtra: typeof rawOpt.renderExtra === 'function' ? rawOpt.renderExtra : undefined,
    };
    drawers.value.push({
      id: `page-content-drawer-${seq}-${Date.now()}`,
      pageCode: page,
      moduleCode: module,
      title: t || '单据详情',
      fullPath,
      stackIndex,
      opt,
    });
  };

  const closeOne = (id: string) => {
    drawers.value = drawers.value.filter((d) => d.id !== id);
  };

  const closeCurrentTab = () => {
    const cur = route.fullPath;
    drawers.value = drawers.value.filter((d) => d.fullPath !== cur);
  };

  /** 关闭指定页签下的抽屉。页签关闭时由 multipleTab 触发，切走再切回不触发。 */
  const closeByFullPath = (fullPath: string) => {
    const path = String(fullPath ?? '').trim();
    if (!path) return;
    drawers.value = drawers.value.filter((d) => d.fullPath !== path);
  };

  watch(
    () => route.fullPath,
    (newPath, oldPath) => {
      if (!oldPath || oldPath === newPath) return;
      suppressAnim.value = true;
      nextTick(() => {
        requestAnimationFrame(() => {
          suppressAnim.value = false;
        });
      });
    },
  );

  onMounted(() => {
    emitter.on('openPageContentDrawer', show);
    emitter.on('closePageContentDrawer', closeCurrentTab);
    emitter.on('closePageContentDrawerByPath', closeByFullPath);
  });

  onBeforeUnmount(() => {
    emitter.off('openPageContentDrawer', show);
    emitter.off('closePageContentDrawer', closeCurrentTab);
    emitter.off('closePageContentDrawerByPath', closeByFullPath);
  });
</script>

<style>
  /*
   * 抽屉 DOM 挂在 #PageLayout。
   * Ant Design Vue 会在 getContainer 内额外创建 portal 容器。该容器必须脱离
   * #PageLayout 的常规子元素布局，否则它会和页面内容各占一整行，导致原页与
   * 切换后的页签被挤出可视区域。
   * 页签内容区 #main-roter-view 默认有 12px 内边距，portal 同步保留该边距，
   * 使抽屉上下边界与背景页签内容一致。
   *
   * 仅「打开中」的抽屉占满内容区；关闭/停靠的绝对不能再 inset:0 挡交互，
   * 否则会盖住页签内容，并干扰切页签体验。
   */
  #PageLayout > .page-content-drawer-portal {
    position: absolute !important;
    inset: 12px !important;
    width: auto !important;
    height: auto !important;
    z-index: 110 !important;
    pointer-events: none;
  }

  #PageLayout .page-content-drawer-wrap.ant-drawer-open {
    position: absolute !important;
    inset: 0;
    z-index: 110;
    pointer-events: auto;
  }

  #PageLayout .page-content-drawer-wrap:not(.ant-drawer-open),
  #PageLayout .page-content-drawer-wrap.is-parked {
    position: absolute !important;
    inset: auto !important;
    width: 0 !important;
    height: 0 !important;
    overflow: hidden !important;
    pointer-events: none !important;
    z-index: -1 !important;
  }

  #PageLayout .page-content-drawer-wrap .ant-drawer-mask,
  #PageLayout .page-content-drawer-wrap .ant-drawer-content-wrapper {
    position: absolute !important;
  }

  #PageLayout .page-content-drawer-wrap .ant-drawer-content-wrapper {
    width: 95% !important;
    box-shadow: -6px 0 16px rgba(0, 0, 0, 0.12);
  }

  #PageLayout .page-content-drawer-wrap .ant-drawer-header {
    padding: 12px 24px;
  }

  #PageLayout .page-content-drawer-wrap.is-no-anim .ant-drawer-content-wrapper,
  #PageLayout .page-content-drawer-wrap.is-no-anim .ant-drawer-mask {
    transition: none !important;
  }

  .page-content-drawer-trace-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    padding: 0 12px;
    line-height: 1;
  }

  .page-content-drawer-trace-btn__icon {
    margin-right: 4px;
    font-size: 14px;
    line-height: 1;
  }
</style>
